"use client";

import React from "react";
import { useTable } from "react-table";
import { twMerge } from "tailwind-merge";
import { NoItems } from "../NoItems";
import { ShowMoreButton } from "../ShowMoreButton";

export interface Props {
  columns: any[];
  data: any[];
  showMoreButton?: boolean;
  showMoreHandler?: () => void;
  isLoading?: boolean;
  hideColumnHeader?: boolean;
  hideColumnHeaderOnMobile?: boolean;
  className?: string;
  headerClassName?: string;
  rowClassName?: string;
  cellClassName?: string;
  dynamicRowClass?: (id: string) => string;
}

const ReactTable = ({
  columns,
  data,
  showMoreButton,
  showMoreHandler,
  hideColumnHeader,
  isLoading,
  hideColumnHeaderOnMobile,
  className,
  headerClassName,
  rowClassName,
  cellClassName,
  dynamicRowClass,
}: Props) => {
  const tableInstance = useTable({ columns, data });

  if (!data?.length && !isLoading) {
    return <NoItems message="No items to display." />;
  }

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance;

  return (
    <div className={twMerge("flex flex-col items-center w-full", className)}>
      <table className="text-left w-full" {...getTableProps()}>
        {!hideColumnHeader && (
          <thead
            className={
              hideColumnHeaderOnMobile ? "hidden lg:table-header-group" : ""
            }
          >
            {headerGroups.map((headerGroup, ix) => (
              <tr
                className="border-b border-grey"
                {...headerGroup.getHeaderGroupProps()}
                key={ix}
              >
                {headerGroup.headers.map((column, ix) => (
                  <th
                    className={twMerge("p-6", headerClassName)}
                    {...column.getHeaderProps()}
                    key={ix}
                  >
                    {column.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
        )}
        <tbody {...getTableBodyProps()}>
          {rows.map((row, ix) => {
            const rowDynamicClass =
              dynamicRowClass && dynamicRowClass(row.original.id);

            prepareRow(row);
            return (
              <tr
                className={twMerge(
                  "border-b border-grey flex lg:table-row",
                  rowClassName,
                  rowDynamicClass,
                )}
                {...row.getRowProps()}
                key={ix}
              >
                {row.cells.map((cell, ix) => {
                  return (
                    <td
                      className={twMerge(
                        "p-6 flex lg:table-cell",
                        cellClassName,
                      )}
                      {...cell.getCellProps()}
                      key={ix}
                    >
                      {cell.render("Cell")}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {showMoreButton && showMoreHandler && (
        <ShowMoreButton fetchNextPage={showMoreHandler} className="mt-20" />
      )}
    </div>
  );
};

export default ReactTable;
